import React, { useEffect, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import validator from 'validator'
import isEmail from 'validator/lib/isEmail'
import { yupResolver } from '@hookform/resolvers/yup'
import freeDomains from 'free-email-domains'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import PasswordRequirements, {
  strongPasswordConfig,
} from 'components/PasswordRequirements'
import Heading from 'subapps/onboarding/layout/Heading'
import { canShowError } from 'subapps/onboarding/utils'
import { doRedirectIfAuthenticated } from '../actions'
import useFieldFocusChange from '../hooks/useFieldFocusChange'
import { styles } from './styles'
import { doSaveAndRedirect } from './actions'

const NAME = 'name'
const EMAIL = 'emailInput'
const PASSWORD = 'password'

// Validation schema
const FORM_SCHEMA = yup.object().shape({
  [NAME]: yup
    .string()
    .required('Must provide your first and last name')
    .test('name', 'Must provide your first and last name', value =>
      /^\D+\s+\D+$/.test(value.trim())
    ),
  [EMAIL]: yup
    .string()
    .required('An email address is required')
    .test('email', 'Please enter your work email address', value =>
      isEmail(value, { host_blacklist: freeDomains })
    ),
  [PASSWORD]: yup
    .string()
    .required('Password is required')
    .test({
      test(value) {
        return validator.isStrongPassword(value?.trim(), strongPasswordConfig)
      },
      message: 'Please enter a valid password',
    }),
})

const defaultValues = {
  [NAME]: '',
  [EMAIL]: '',
  [PASSWORD]: '',
}

const Emailpage = () => {
  const dispatch = useDispatch()
  const {
    register,
    watch,
    handleSubmit,
    setFocus,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(FORM_SCHEMA),
  })
  const [focusedFields, setFocusedFields] = useState(null)
  const nameProps = useFieldFocusChange(register(NAME), setFocusedFields)
  const emailProps = useFieldFocusChange(register(EMAIL), setFocusedFields)
  const passwordProps = useFieldFocusChange(
    register(PASSWORD),
    setFocusedFields
  )
  const submitDisabled = !isDirty || !isValid || isSubmitting
  const nameValue = watch(NAME)
  const emailValue = watch(EMAIL)
  const passwordValue = watch(PASSWORD)
  const nameError =
    errors[NAME] && canShowError(focusedFields, NAME, nameValue)
      ? errors[NAME].message
      : undefined
  const emailError =
    errors[EMAIL] && canShowError(focusedFields, EMAIL, emailValue)
      ? errors[EMAIL].message
      : undefined
  const passwordError =
    errors[PASSWORD] && canShowError(focusedFields, PASSWORD, passwordValue)
      ? errors[PASSWORD].message
      : undefined

  const onSubmit = useCallback(
    async data => {
      // await, so isSubmitting can be true during submitting
      await dispatch(doSaveAndRedirect(data))
    },
    [dispatch]
  )

  useEffect(
    () => {
      setFocus(NAME)
    },
    [setFocus]
  )

  useEffect(
    () => {
      dispatch(doRedirectIfAuthenticated(true))
    },
    [dispatch]
  )

  return (
    <>
      <Heading className="grui mb-12 mt-14" />
      <div css={styles.container}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Field
            label="Full Name"
            placeholder="What’s your full name?"
            className="grui mb-10"
            css={styles.field}
            validateStatus={nameError ? 'error' : undefined}
            help={nameError}
            {...nameProps}
            autoComplete="off"
          />
          <Field
            label="Work email"
            htmlType="email"
            placeholder="What’s your work email address?"
            className="grui mb-10"
            css={styles.field}
            validateStatus={emailError ? 'error' : undefined}
            help={emailError}
            {...emailProps}
            autoComplete="off"
          />
          <div>
            <Field
              label="Password"
              htmlType="password"
              placeholder="Enter 8 characters or more"
              className="grui mb-10"
              css={styles.field}
              validateStatus={passwordError ? 'error' : undefined}
              {...passwordProps}
              autoComplete="off"
            />
            <PasswordRequirements
              value={passwordValue}
              css={styles.requirements}
            />
          </div>
          <Button
            className="grui mt-12"
            htmlType="submit"
            type="info"
            size="xBig"
            css={styles.submitButton}
            disabled={submitDisabled}
          >
            Get started
          </Button>
        </form>
      </div>
    </>
  )
}

export default Emailpage
